import { uniq } from 'lodash'
import {
  INITIALIZE_APPLICATION,
  ADD_MFA_MECHANISM_SUCCEEDED,
  RECOVERY_OTPS_REGISTER,
  RECOVERY_OTPS_REGISTER_SUCCEEDED,
  RECOVERY_OTPS_REGISTER_FAILED,
  RECOVERY_OTPS_RENEW,
  REVOKE_MFA_MECHANISM_SUCCEEDED,
  REVOKE_MFA_MECHANISM_FAILED,
  REVOKE_MFA_CONFIRMATION_DISMISSED,
  SELECT_MFA_TO_REVOKE
} from '../constants/action.types'
import { RECOVERY_OTP } from '../constants/mfa'

const isMFAEnabled = (enabledMFAMechanisms = [], isLocalLoginEnabled = false) =>
  enabledMFAMechanisms.filter(mfa => mfa !== RECOVERY_OTP).length > 0 &&
  isLocalLoginEnabled

const getMFASettingsFromUser = ({
  user: {
    enabled_mfa_mechanisms,
    mfa_enforced: mfaEnforced = false,
    local_login_enabled: isLocalLoginEnabled = false
  } = {}
}) => {
  const enabledMFAMechanisms =
    (isLocalLoginEnabled && enabled_mfa_mechanisms) || []

  const hasMFAEnabled = isMFAEnabled(enabledMFAMechanisms, isLocalLoginEnabled)

  return {
    enabledMFAMechanisms,
    mfaEnforced,
    hasMFAEnabled
  }
}

const getCommonSettingsFromUser = ({
  local_login_enabled: isLocalLoginEnabled = false
} = {}) => ({
  isLocalLoginEnabled
})

const removeMFAMechanisms = (
  { enabledMFAMechanisms = [] },
  { mfaMechanism }
) => {
  if (mfaMechanism === RECOVERY_OTP) return { enabledMFAMechanisms: [] }

  return {
    enabledMFAMechanisms: enabledMFAMechanisms.filter(
      mfa => mfa !== mfaMechanism
    )
  }
}

const addMFAMechanism = ({ enabledMFAMechanisms = [] }, { mfaMechanism }) => {
  if (!mfaMechanism) return { enabledMFAMechanisms }

  const newMfaMechanisms = [...enabledMFAMechanisms, mfaMechanism]

  return {
    enabledMFAMechanisms: uniq(newMfaMechanisms),
    hasMFAEnabled: true
  }
}

const initialMFAState = {
  enabledMFAMechanisms: [],
  mfaEnforced: false,
  hasMFAEnabled: false,
  isMFASetupInProgress: false,
  recoveryOTPRegisterErrorCode: null,
  revokeMFAErrorCode: null,
  mfaToRevoke: RECOVERY_OTP,
  isMFARevokeInProgress: false,
  isSuccessfullyRevoked: false,
  recoveryCodeList: null
}

const mfaSettingsReducer = (state, { type, payload = {} }) => {
  const { mfaSettings: mfaState } = state || {}
  const newMfaState = {
    [INITIALIZE_APPLICATION]: {
      ...getMFASettingsFromUser(payload)
    },
    [RECOVERY_OTPS_REGISTER]: {
      isMFASetupInProgress: true
    },
    [RECOVERY_OTPS_REGISTER_SUCCEEDED]: {
      recoveryCodeList: payload.recoveryCodeList,
      isRecoveryCodesRenewInProgress: false
    },
    [RECOVERY_OTPS_REGISTER_FAILED]: {
      recoveryOTPRegisterErrorCode: payload.errorCode,
      isRecoveryCodesRenewInProgress: false
    },
    [RECOVERY_OTPS_RENEW]: {
      isRecoveryCodesRenewInProgress: true
    },
    [REVOKE_MFA_MECHANISM_SUCCEEDED]: {
      ...removeMFAMechanisms(mfaState || {}, payload),
      isSuccessfullyRevoked: true
    },
    [REVOKE_MFA_MECHANISM_FAILED]: {
      revokeMFAErrorCode: payload.errorCode
    },
    [REVOKE_MFA_CONFIRMATION_DISMISSED]: {
      hasMFAEnabled: isMFAEnabled(
        mfaState && mfaState.enabledMFAMechanisms,
        state && state.isLocalLoginEnabled
      ),
      revokeMFAErrorCode: null,
      isSuccessfullyRevoked: false
    },
    [ADD_MFA_MECHANISM_SUCCEEDED]: {
      ...addMFAMechanism(mfaState || {}, payload)
    },
    [SELECT_MFA_TO_REVOKE]: {
      mfaToRevoke: payload.mfaToRevoke,
      revokeMFAErrorCode: null
    }
  }[type]

  return {
    ...(mfaState || initialMFAState),
    isMFASetupInProgress: false,
    isMFARevokeInProgress: false,
    isSuccessfullyRevoked: false,
    recoveryOTPRegisterErrorCode: null,
    recoveryCodeList: null,
    ...newMfaState
  }
}

const passwordSettingsReducer = state => ({
  ...state,
  isPasswordSetupInProgress: false,
  errorCode: null
})

const commonSettingsReducer = (state, { type, payload = {} }) => {
  const newState = {
    [INITIALIZE_APPLICATION]: {
      ...getCommonSettingsFromUser(payload.user)
    }
  }[type]

  return {
    ...state,
    ...newState
  }
}

export default (state, action) => {
  return {
    ...commonSettingsReducer(state, action),
    passwordSettings: passwordSettingsReducer(
      state && state.passwordSettings,
      action
    ),
    mfaSettings: mfaSettingsReducer(state, action)
  }
}
